/**
 * Number formatting utilities for display values.
 *
 * Story 4.2: Token Detail Page | Story 4.4: Trade History and Holder List
 */

import { formatUnits } from "viem";

/**
 * Format a USD price with appropriate decimal places.
 * - >= $1: 2 decimals ($45.67)
 * - >= $0.01: 4 decimals ($0.0123)
 * - < $0.01: 6 decimals ($0.001234)
 */
export function formatPrice(price: number | null | undefined): string {
	if (price === null || price === undefined) return "--";
	if (price >= 1)
		return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	if (price >= 0.01) return `$${price.toFixed(4)}`;
	return `$${price.toFixed(6)}`;
}

/**
 * Format a price change percentage with sign and color class.
 */
export function formatPriceChange(change: number | null | undefined): {
	text: string;
	className: string;
} {
	if (change === null || change === undefined) return { text: "--", className: "text-pastel-gray" };
	if (change > 0) return { text: `+${change.toFixed(1)}%`, className: "text-green-600" };
	if (change < 0) return { text: `${change.toFixed(1)}%`, className: "text-red-500" };
	return { text: "0.0%", className: "text-pastel-gray" };
}

/**
 * Format a large number with abbreviated suffix.
 * - >= 1B: "1.2B"
 * - >= 1M: "1.2M"
 * - >= 1K: "45.3K"
 * - < 1K: "123"
 */
export function formatCompact(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
	if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
	if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
	return `$${value.toFixed(0)}`;
}

/**
 * Format a count with comma separators.
 */
export function formatCount(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	return value.toLocaleString();
}

/**
 * Format a token amount with abbreviated suffix (no $ prefix).
 * - >= 1B: "1.2B"
 * - >= 1M: "1.2M"
 * - >= 1K: "45.3K"
 * - < 1K, >= 1: "123.45"
 * - < 1: up to 6 decimals
 */
export function formatTokenAmount(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
	if (value >= 1) return value.toFixed(2);
	if (value >= 0.01) return value.toFixed(4);
	return value.toFixed(6);
}

/**
 * Format an ETH amount with 4 decimal places.
 */
export function formatEth(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	return `${value.toFixed(4)} ETH`;
}

/**
 * Format a wei-denominated amount with a currency symbol.
 * Uses compact notation for large amounts, full precision for small.
 *
 * @param weiAmount - raw bigint in wei (18 decimals)
 * @param symbol - currency symbol to append (e.g. "ETH", "A0GI")
 */
export function formatNativeCurrency(weiAmount: bigint, symbol: string): string {
	const numericValue = Number(formatUnits(weiAmount, 18));
	if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(1)}K ${symbol}`;
	if (numericValue >= 1) return `${numericValue.toFixed(2)} ${symbol}`;
	if (numericValue > 0) return `${numericValue.toFixed(4)} ${symbol}`;
	return `0 ${symbol}`;
}

/**
 * Format a percentage value with one decimal.
 */
export function formatPercent(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	return `${value.toFixed(1)}%`;
}

/**
 * Format a native currency amount with compact suffix.
 * - >= 1000: "1.2K"
 * - >= 1: "1.23"
 * - > 0: "0.0012"
 * - 0: "0"
 */
export function formatNativeAmount(value: number): string {
	if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
	if (value >= 1) return value.toFixed(2);
	if (value > 0) return value.toFixed(4);
	return "0";
}

/**
 * Format a relative time string from an ISO date.
 * Returns "Created X ago" style strings.
 */
export function formatRelativeTime(isoDate: string): string {
	const date = new Date(isoDate);
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${String(minutes)}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${String(hours)}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${String(days)}d ago`;
	const months = Math.floor(days / 30);
	return `${String(months)}mo ago`;
}
